import type { BrowserPermissionState } from './webPlatformFeatures'

/**
 * Browser permission state for the worker Permissions Center, modeled on the
 * Android permission UX.
 *
 * A worker who explicitly rejects a permission is never nagged again: the
 * rejection is remembered locally and only an explicit tap re-requests. This
 * mirrors the Android app, which stops prompting once the OS reports a
 * permanent denial.
 */

export type PermissionSubject = 'geolocation' | 'notifications' | 'camera' | 'microphone'

const DECLINED_STORAGE_KEY = 'punch.worker.permissions.declined'

const permissionDescriptorName: Partial<Record<PermissionSubject, PermissionName>> = {
  geolocation: 'geolocation' as PermissionName,
  notifications: 'notifications' as PermissionName,
  camera: 'camera' as PermissionName,
  microphone: 'microphone' as PermissionName,
}

const readDeclined = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(window.localStorage.getItem(DECLINED_STORAGE_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

export function hasDeclined(subject: PermissionSubject): boolean {
  return readDeclined()[subject] === true
}

export function rememberDeclined(subject: PermissionSubject, declined = true): void {
  if (typeof window === 'undefined') return
  try {
    const current = readDeclined()
    if (declined) current[subject] = true
    else delete current[subject]
    window.localStorage.setItem(DECLINED_STORAGE_KEY, JSON.stringify(current))
  } catch {
    // Private-mode storage failures must never block a permission flow.
  }
}

/**
 * Query without prompting. Safari does not implement `permissions.query` for
 * every descriptor, so an unsupported query is reported as `unknown` rather
 * than being misread as denied.
 */
export async function queryPermission(subject: PermissionSubject): Promise<BrowserPermissionState> {
  if (typeof navigator === 'undefined') return 'unsupported'
  if (subject === 'notifications' && typeof window !== 'undefined' && 'Notification' in window) {
    const current = (window as any).Notification?.permission
    if (current === 'granted') return 'granted'
    if (current === 'denied') return 'denied'
    return 'prompt'
  }
  if (subject === 'geolocation' && !navigator.geolocation) return 'unsupported'
  if ((subject === 'camera' || subject === 'microphone') && !navigator.mediaDevices?.getUserMedia) return 'unsupported'
  const permissions = (navigator as any).permissions
  const name = permissionDescriptorName[subject]
  if (!permissions?.query || !name) return 'unknown'
  try {
    const status = await permissions.query({ name })
    if (status?.state === 'granted' || status?.state === 'denied' || status?.state === 'prompt') {
      return status.state
    }
    return 'unknown'
  } catch {
    // Descriptor unsupported by this engine (common on Safari).
    return 'unknown'
  }
}

export async function queryAllPermissions(): Promise<Record<PermissionSubject, BrowserPermissionState>> {
  const subjects: PermissionSubject[] = ['geolocation', 'notifications', 'camera', 'microphone']
  const entries = await Promise.all(subjects.map(async (subject) => [subject, await queryPermission(subject)] as const))
  return Object.fromEntries(entries) as Record<PermissionSubject, BrowserPermissionState>
}

/** Explicit user-initiated request only. Never call this on page load. */
export async function requestPermission(subject: PermissionSubject): Promise<BrowserPermissionState> {
  if (typeof navigator === 'undefined') return 'unsupported'
  try {
    if (subject === 'notifications') {
      if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
      const result = await (window as any).Notification.requestPermission()
      const state: BrowserPermissionState = result === 'granted' ? 'granted' : result === 'denied' ? 'denied' : 'prompt'
      rememberDeclined(subject, state === 'denied')
      return state
    }
    if (subject === 'geolocation') {
      if (!navigator.geolocation) return 'unsupported'
      const state = await new Promise<BrowserPermissionState>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve('granted'),
          (error) => resolve(error.code === error.PERMISSION_DENIED ? 'denied' : 'prompt'),
          { enableHighAccuracy: false, timeout: 15_000, maximumAge: 60_000 }
        )
      })
      rememberDeclined(subject, state === 'denied')
      return state
    }
    if (!navigator.mediaDevices?.getUserMedia) return 'unsupported'
    const constraints = subject === 'camera' ? { video: true } : { audio: true }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    // The probe only establishes the grant; release the hardware immediately.
    stream.getTracks().forEach((track) => track.stop())
    rememberDeclined(subject, false)
    return 'granted'
  } catch (error) {
    const name = (error as { name?: string })?.name
    const denied = name === 'NotAllowedError' || name === 'SecurityError'
    rememberDeclined(subject, denied)
    return denied ? 'denied' : 'prompt'
  }
}
